import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from 'state/modules';

type Setting = { key: string; value: number; name: string };
type SettingsByKey = Record<string, { key: string; value: number }>;

const EMPTY_OBJECT: SettingsByKey = Object.freeze({});

/**
 * Comparison function to facilitate the sorting
 * of settings by name.
 * @param  {Object} a
 * @param  {Object} b
 * @return {Number}
 */
const sortByName = (a: { name: string }, b: { name: string }) => {
  if (a.name < b.name) {
    return -1;
  }

  if (a.name > b.name) {
    return 1;
  }

  return 0;
};

/**
 * Get all the settings for the current user.
 * @param  {Object} state
 * @return {Object}
 */
export const forUser = (state: RootState) => state.settings.forUser;

/**
 * Get all the setting for a brokerage.
 * @param  {Object} state
 * @param  {String} brokerageId
 * @return {Object}
 */
const getAllByBrokerageId = (state: RootState, brokerageId: string) =>
  state.settings.byBrokerageId[brokerageId] ?? EMPTY_OBJECT;

/**
 * Get All Settings from state.
 * @param  {Object} state
 * @return {Object}
 */
const getAllSettings = (state: RootState) => state.settings.byId as Record<string, Setting>;

/**
 * Get the setting type passed to the selectors.
 * NOTE: This is a work around to pass the value to the
 * memorized selector created with reselect.
 * @param  {Object} state
 * @param  {String} type
 * @return {String}
 */
const getSettingType = (state: RootState, type = 'brokerage') => type;

export const getActiveByBrokerageId = createSelector([getAllByBrokerageId], (settings) =>
  Object.keys(settings)
    .map((settingId) => settings[settingId])
    .filter((setting) => setting.value >= 1)
    .map((setting) => setting.key)
);

/**
 * Get All settings from state.
 * @param {Object} state
 * @param {String} type
 * @return {Array}
 */
export const getAll = createSelector([getAllSettings, getSettingType], (settings, type) =>
  Object.keys(settings)
    .map((settingId) => settings[settingId])
    .filter((setting) => setting.key.includes(type))
    .sort(sortByName)
);

/**
 * Get the isLoading state for settings.
 * @param {Object} state
 */
export const isLoading = (state: RootState) => state.settings.pendingRequestsCount !== 0;
